import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import "./home.scss";
import Widget from "../../components/widget/Widget";
import UpdateBox from "../../components/infoBox/UpdateBox";
import { useEffect, useState, useContext } from "react";
import { collection, query, getDocs, where, doc, getDoc, limit, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import { AuthContext } from "../../context/AuthContext";
import BarChartWidget from '../../components/chart/BarChartWidget';
import { useSidebarState } from "../../components/sidebar/SidebarStateContext";
import { Menu, X } from "lucide-react"; // Import icons (install if needed)

const Home = () => {
  const [widgetData, setWidgetData] = useState({
    vigilantes: { amount: null, growthPercentage: 0, displayName: "USUARIOS" },
    tiendas: { amount: null, growthPercentage: 0 },
    documents: { amount: null, growthPercentage: 0 },
  });
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [servicioId, setServicioId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showContent, setShowContent] = useState(false);

  const { currentUser, adminData } = useContext(AuthContext);
  const { collapsed } = useSidebarState();

  // Detect if we're on mobile and set initial state
  useEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setShowContent(!mobile); // Only show content initially if not on mobile
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Determine container class based on sidebar state and device type
  const containerClass = () => {
    let baseClass = collapsed ? "homeContainer collapsed" : "homeContainer";
    
    if (isMobile) {
      baseClass += showContent ? " visible" : "";
    }
    
    return baseClass;
  };

  const toggleMobileContent = () => {
    setShowContent(!showContent);
  };

  const chartData = Object.keys(widgetData).map(key => ({
    name: key,
    amount: widgetData[key].amount,
    growthPercentage: widgetData[key].growthPercentage,
  }));

  useEffect(() => {
    const fetchServicioId = async () => {
      if (currentUser?.email) {
        const serviciosRef = collection(db, "servicios");
        const q = query(serviciosRef, where("admins", "array-contains", currentUser.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const servicioDoc = querySnapshot.docs[0];
          setServicioId(servicioDoc.id);
        }
      }
    };

    fetchServicioId();
  }, [currentUser]);

  useEffect(() => {
    if (servicioId) {
      fetchData("vigilantes");
      fetchData("tiendas");
      fetchData("documents");
      fetchRecentDocuments();
    }
  }, [servicioId]);

  const fetchRecentDocuments = async () => {
    if (!adminData?.clientes || !servicioId) return;

    try {
      // Fetch client names from the "clientes" collection
      const clientNamesPromises = adminData.clientes.map(async clientId => {
        if (!clientId) return null;
        
        const clientDocRef = doc(db, "servicios", servicioId, "clientes", clientId);
        const clientDocSnapshot = await getDoc(clientDocRef);

        if (clientDocSnapshot.exists()) {
          return clientDocSnapshot.data().nombre;
        }
        return null;
      });

      const clientNames = await Promise.all(clientNamesPromises);
      const validClientNames = clientNames.filter(name => name !== null);

      // Obtener todos los documentos y ordenarlos por fecha
      const documentsRef = collection(db, "servicios", servicioId, "documents");
      const q = query(
        documentsRef,
        orderBy("fecha", "desc"),
        limit(15) // Traer un poco más para asegurarnos de tener 5 válidos después del filtrado
      );
      
      const querySnapshot = await getDocs(q);
      const allDocs = [];
      
      // Procesar los documentos
      for (const docSnapshot of querySnapshot.docs) {
        const documentData = docSnapshot.data();
        
        // Verificar si el documento pertenece a uno de nuestros clientes
        if (!documentData?.vigilante || !validClientNames.includes(documentData.cliente)) continue;

        const vigilanteId = documentData.vigilante.replace('@gmail.com', '');
        const vigilanteQuery = query(
          collection(db, "servicios", servicioId, "vigilantes"), 
          where("tipVS", "==", vigilanteId)
        );
        
        const vigilanteSnapshot = await getDocs(vigilanteQuery);

        if (!vigilanteSnapshot.empty) {
          const vigilanteData = vigilanteSnapshot.docs[0].data();
          allDocs.push({ ...documentData, vigilanteData });
        } else {
          allDocs.push(documentData);
        }
        
        // Detener cuando tengamos 5 documentos válidos
        if (allDocs.length >= 5) break;
      }

      setRecentDocuments(allDocs);
    } catch (error) {
      console.error("Error fetching recent documents:", error);
    }
  };

  const fetchData = async (type) => {
    if (!servicioId) return;

    try {
      const monthlyDataRef = doc(db, 'monthlyData', servicioId, type, 'currentMonth');
      const monthlyDataDoc = await getDoc(monthlyDataRef);

      if (monthlyDataDoc.exists()) {
        const monthlyData = monthlyDataDoc.data();
        setWidgetData(prevData => ({
          ...prevData,
          [type]: {
            amount: monthlyData.totalAllTime || 0,
            growthPercentage: monthlyData.totalLastMonth || 0,
          }
        }));
      }
    } catch (error) {
      console.error(`Error fetching ${type} data:`, error);
    }
  };

  function formatDate(timestamp) {
    if (!timestamp?.toDate) return '';
    const date = timestamp.toDate();
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('es-ES', options);
  }

  return (
    <div data-testid="sidebar1" className="home">
      <Sidebar />
      
      {/* Mobile Toggle Button (Only visible on mobile) */}
      {isMobile && (
        <button 
          className="mobile-toggle-btn" 
          onClick={toggleMobileContent}
          style={{
            position: 'fixed',
            right: showContent ? '20px' : '20px',
            top: '20px',
            zIndex: 1000,
            background: '#1278bd',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}
        >
          {showContent ? <X size={24} /> : <Menu size={24} />}
        </button>
      )}
      
      <div data-testid="navbar1" className={containerClass()}>
        <Navbar />
        <div className="user-greeting">
          <h2>Hola {currentUser?.displayName || currentUser?.email || 'Usuario'} !</h2>
        </div>
        <div data-testid="widget" className="widgets">
          <Widget type="vigilantes" data={widgetData.vigilantes} />
          <Widget type="tiendas" data={widgetData.tiendas} />
          <Widget type="documents" data={widgetData.documents} />
        </div>
        <BarChartWidget data={chartData} />

        <div className="box-container">
          <div className="service-info-box recent-documents">
            <h2>Última actividad</h2>
            <ul>
              {recentDocuments.map((doc, index) => (
                <li key={index} className="document-item">
                  <div className="info">
                    <div className="name-text">
                      <span className="blue-text">{doc.documentTypeId}</span>
                      <p>
                        {doc.vigilanteData?.nombre} {doc.vigilanteData?.apellidos}
                      </p>
                    </div>
                    <p className="date-text">{formatDate(doc.fecha)}</p>
                  </div>
                </li>
              ))}
              {recentDocuments.length === 0 && (
                <li className="document-item no-data">
                  <div className="info">
                    <p>No hay actividad reciente</p>
                  </div>
                </li>
              )}
            </ul>
          </div>
          <UpdateBox />
        </div>
      </div>
    </div>
  );
};

export default Home;